import { useState, useEffect } from 'react';
import notificationsApi from './notificationsApi';
import { onDataChanged, onScreenResumed } from '../utils/dataChangeSignal';

// Custom hook to manage notification count
export const useNotificationCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // currentUser is available from context if needed in the future
  // const { currentUser } = useContext(AuthContext);

  const fetchUnreadCount = async () => {
    try {
      setIsLoading(true);
      // businessId and userId now come from session context automatically
      const res = await notificationsApi.getNotifications({ 
        status: 'unread', 
        limit: 100 
      });
      
      if (res && res.success) {
        const unreadNotifications = (res.data || []).filter(n => !n.is_read);
        setUnreadCount(unreadNotifications.length);
      } else {
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch unread notification count:', error);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    // Optimistically update the count
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setUnreadCount(0);
  };

  const incrementCount = () => {
    setUnreadCount(prev => prev + 1);
  };

  // The badge is refreshed on events rather than every 30 seconds.
  //
  // Counting unread notifications means pulling up to 100 rows, and this hook is
  // mounted twice over (the dashboard bell and the notifications screen), so the
  // timer was running that query four times a minute for a number that changes a
  // handful of times a day.
  //
  // Almost every notification is raised by a write, and a write announces itself
  // here and in other tabs. What no browser can know about is one raised by a
  // server-side job while nobody is touching anything — so the count is also
  // re-read when someone comes back to the tab, which is the first moment a
  // stale badge could be seen. Opening the notifications screen refreshes it too.
  useEffect(() => {
    fetchUnreadCount();

    const refresh = () => { fetchUnreadCount(); };
    const stopListening = onDataChanged(refresh);
    const stopResuming = onScreenResumed(refresh);

    return () => {
      stopListening();
      stopResuming();
    };
  }, []);

  return {
    unreadCount,
    isLoading,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    incrementCount
  };
};
